import { BrowserModule } from '@angular/platform-browser';
import { CommonModule, APP_BASE_HREF, TitleCasePipe } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { OverlayModule } from '@angular/cdk/overlay';
import { DragDropModule } from '@angular/cdk/drag-drop';

// 3rd party
import { AppRoutingModule, EmptyRouteComponent } from './/app-routing.module';
import { AgGridModule } from 'ag-grid-angular';
import { CdkTreeModule } from '@angular/cdk/tree';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { DndModule } from 'ngx-drag-drop';
import { ModalModule, BsModalRef } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { HighlightModule, HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
// Components
import { AppComponent } from './app.component';
import { ActiveUserSessionsComponent } from './components/status-management/active-user-sessions/active-user-sessions.component';
import { DocumentViewerComponent } from './components/document-viewer/document-viewer.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { ClusterListComponent } from './components/cluster-list/cluster-list.component';
import { ConnectorsAndTreeComponent } from './components/connectors-and-tree/connectors-and-tree.component';
import { ConnectorPickerComponent } from './components/connector-picker/connector-picker.component';
import { DiscardPileLogComponent } from './components/modals/discard-pile-log-modal/discard-pile-log-modal.component';
import { DocumentListComponent } from './components/document-list/document-list.component';
import { EmailThreadsComponent } from './components/email-threads/email-threads.component';
import { ExceptionListComponent } from './components/exception-list/exception-list.component';
import { ExportStreamVolumeSettingsComponent } from './components/export-stream-volume-settings/export-stream-volume-settings.component';
import { JobHistoryComponent } from './components/status-management/job-history/job-history.component';
import { JobManagementComponent } from './components/status-management/job-management/job-management.component';
import { LoginComponent } from './components/login/login.component';
import { MainContentComponent } from './components/main-content/main-content.component';
import { MatrixComponent } from './components/matrix-component/matrix.component';
import { MonitoringComponent } from './components/status-management/monitoring/monitoring.component';
import { NameDescriptionComponent } from './components/name-description/name-description.component';
import { NewDataSetLoadFileMenuComponent } from './components/new-data-set-load-file-menu/new-data-set-load-file-menu.component';
import { ProfileDetailComponent } from './components/profile-detail/profile-detail.component';
import { ProjectListComponent } from './components/project-list/project-list.component';
import { ProjectAccessHistoryComponent } from './components/status-management/project-access-history/project-access-history.component';
import { ReportsComponent } from './components/reports/reports.component';
import { ResizerComponent } from './components/resizer/resizer.component';
import { ShortMessageThreadsComponent } from './components/short-message-threads/short-message-threads.component';
import { TaskFilterComponent } from './components/workbasket/task-filter/task-filter.component';
import { TopNavBarComponent } from './components/top-nav-bar/top-nav-bar.component';
import { ToastNotificationComponent } from './components/toast-notification/toast-notification.component';
import { SummaryComponent } from './components/summary/summary.component';
import { StatusManagementComponent } from './components/status-management/status-management.component';
import { WorkbasketComponent } from './components/workbasket/workbasket.component';
import { WorkflowComponent } from './components/workflow/workflow.component';
import { SystemAuditLogComponent } from './components/status-management/system-audit-log/system-audit-log.component';

// Subcomponents
import { BulkSearchComponent } from './components/bulk-search/bulk-search.component';
import { CollapsibleToggleBoxGroupComponent } from './components/collapsible-toggle-box-group/collapsible-toggle-box-group.component';
import { CustomCheckboxComponent } from './components/custom-checkbox/custom-checkbox.component';
import { CustomRadioButtonComponent } from './components/custom-radio-button/custom-radio-button.component';
import { CustomComboBoxComponent } from './components/custom-combo-box/custom-combo-box.component';
import { CustomInputComponent } from './components/custom-input/custom-input.component';
import { CustomInputFileComponent } from './components/custom-input-file/custom-input-file.component';
import { CustomSelectComponent } from './components/custom-select/custom-select.component';
import { CustomSliderComponent } from './components/custom-slider/custom-slider.component';
import { CustomDateTimePickerComponent } from './components/custom-date-time-picker/custom-date-time-picker.component';
import { CustomDateTimeRangePickerComponent } from './components/custom-date-time-range-picker/custom-date-time-range-picker.component';
import { DataAreaAndTreeComponent } from './components/data-area-and-tree/data-area-and-tree.component';
import { ExportComponent } from './components/export/export.component';
import { ExportOutputFormattingOptionsComponent } from './components/export/export-output-formatting-options/export-output-formatting-options.component';
import { ExportProductionImagingOptionsComponent } from './components/export/export-production-imaging-options/export-production-imaging-options.component';
import { FilterComponent } from './components/filter/filter.component';
import { GenericGridComponent } from './components/generic-grid/generic-grid.component';
import { GenericInputComponent } from './components/generic-input/generic-input.component';
import { GenericTreeComponent } from './components/generic-tree/generic-tree.component';
import { GridCellToggleSwitchComponent } from './components/grid-cell-toggle-switch/grid-cell-toggle-switch.component';
import { KeyValueListComponent } from './components/key-value-list/key-value-list.component';
import { MetadataFiltersComponent } from './components/metadata-filters/metadata-filters.component';
import { PagerComponent } from './components/pager/pager.component';
import { LeftRightSelectionBoxComponent } from './components/left-right-selection-box/left-right-selection-box.component';
import { SearchComponent } from './components/search/search.component';
import { TargetPickerTreeComponent } from './components/target-picker-tree/target-picker-tree.component';
import { WorkflowStepComponent } from './components/workflow/step/workflow-step.component';

// Grid Templates
import { ComboBoxCellComponent } from './components/grid-templates/combo-box-cell/combo-box-cell.component'
import { DragCellComponent } from './components/grid-templates/drag-cell/drag-cell.component';
import { DragDropCellComponent } from './components/grid-templates/drag-drop-cell/drag-drop-cell.component';
import { GridHeaderComponent } from './components/grid-templates/grid-header/grid-header.component';

// Modals
import { AddAliasesDomainEntriesModalComponent } from './components/modals/add-aliases-domain-entries-modal/add-aliases-domain-entries-modal.component';
import { AddPasswordsModalComponent } from './components/modals/add-passwords-modal/add-passwords-modal.component';
import { AddSelectedTextToModalComponent } from './components/modals/add-selected-text-to-modal/add-selected-text-to-modal.component';
import { AddSharedDataSetModalComponent } from './components/modals/add-shared-data-set-modal/add-shared-data-set-modal.component';
import { AddToRemoveFromModalComponent } from './components/modals/add-to-remove-from-modal/add-to-remove-from-modal.component';
import { AdvancedSearchModalComponent } from './components/search/advanced-search-modal/advanced-search-modal.component';
import { AuditLogModalComponent } from './components/modals/audit-log-modal/audit-log-modal.component';
import { BuildClustersModalComponent } from './components/modals/build-clusters-modal/build-clusters-modal.component';
import { CreateDatasetFromSearchResultModalComponent } from './components/modals/create-dataset-from-search-result-modal/create-dataset-from-search-result-modal.component';
import { CreateEditComparisonModalComponent } from './components/modals/create-edit-comparison-modal/create-edit-comparison-modal.component';
import { CreateEditEntityGroupModalComponent } from './components/modals/create-edit-entity-group-modal/create-edit-entity-group-modal.component';
import { CreateEditEntityModalComponent } from './components/modals/create-edit-entity-modal/create-edit-entity-modal.component';
import { CreateEditGroupFieldModalComponent } from './components/modals/create-edit-group-field-modal/create-edit-group-field-modal.component';
import { CreateManifestModalComponent } from './components/modals/create-manifest/create-manifest.component';
import { CreateNearDuplicateMetadataModalComponent } from './components/modals/create-near-duplicate-metadata-modal/create-near-duplicate-metadata-modal.component';
import { CreateSampleModalComponent } from './components/modals/create-sample-modal/create-sample-modal.component';
import { ChangePasswordModalComponent } from './components/modals/change-password-modal/change-password-modal.component';
import { ChangeServiceModalComponent } from './components/modals/change-service-modal/change-service-modal.component';
import { ConnectorsListComponent } from './components/modals/settings-modal/connectors-list/connectors-list.component';
import { ConnectorsModalComponent } from './components/modals/connectors-modal/connectors-modal.component';
import { ConnectorPickerModalComponent } from './components/modals/connector-picker-modal/connector-picker-modal.component';
import { CopyToDocStorageModalComponent } from './components/modals/copy-to-doc-storage-modal/copy-to-doc-storage-modal.component';
import { CopyToLoadFromExternalAreaModalComponent } from './components/modals/copy-to-load-from-external-area-modal/copy-to-load-from-external-area-modal.component';
import { DataAreaModalComponent } from './components/modals/data-area-modal/data-area-modal.component';
import { DataSetConfigurationModalComponent } from './components/modals/data-set-configuration-modal/data-set-configuration-modal.component';
import { DataSetModalComponent } from './components/modals/data-set-modal/data-set-modal.component';
import { DeleteEntityModalComponent } from './components/modals/delete-entity-modal/delete-entity-modal.component';
import { DownloadAsPDFStampModalComponent } from './components/modals/download-as-pdf-stamp-modal/download-as-pdf-stamp-modal.component';
import { DownloadOCRUsageReportModalComponent } from './components/modals/download-ocr-usage-report-modal/download-ocr-usage-report-modal.component';
import { DownloadWordListModalComponent } from './components/modals/download-word-list-modal/download-word-list-modal.component';
import { EditBucketModalComponent } from './components/modals/edit-bucket/edit-bucket-modal.component';
import { EnterSecurityCodeModalComponent } from './components/modals/enter-security-code-modal/enter-security-code-modal.component';
import { EndUserLicenseAgreementModalComponent } from './components/modals/end-user-license-agreement-modal/end-user-license-agreement-modal.component';
import { ExcludedContentModalComponent } from './components/modals/excluded-content-modal/excluded-content-modal.component';
import { ExportDuplicatesVolumeModalComponent } from './components/modals/export-duplicates-volume-modal/export-duplicates-volume-modal.component';
import { ExportModalComponent } from './components/modals/export-modal/export-modal.component';
import { ExportProjectModalComponent } from './components/modals/export-project-modal/export-project-modal.component';
import { ExportToReviewModalComponent } from './components/modals/export-to-review-modal/export-to-review-modal.component';
import { FindInTreeModalComponent } from './components/modals/find-in-tree-modal/find-in-tree-modal.component';
import { FoundPasswordsModalComponent } from './components/modals/found-passwords-modal/found-passwords-modal.component';
import { GenerateMoreReportsModalComponent } from './components/modals/generate-more-reports-modal/generate-more-reports-modal.component';
import { GenerateDataSetReportModalComponent } from './components/modals/generate-data-set-report-modal/generate-data-set-report-modal.component';
import { GenericPickerModalComponent } from './components/modals/generic-picker-modal/generic-picker-modal.component';
import { ImportProjectModalComponent } from './components/modals/import-project-modal/import-project-modal.component';
import { NotificationModalComponent } from './components/modals/notification-modal/notification-modal.component';
import { KeyFileModalComponent } from './components/modals/key-file-modal/key-file-modal.component';
import { KnowFrequentlyUsedPasswordsModalComponent } from './components/modals/known-frequently-used-passwords-modal/known-frequently-used-passwords-modal.component';
import { LiveLogModalComponent } from './components/modals/live-log-modal/live-log-modal.component';
import { MembershipModalComponent } from './components/modals/membership-modal/membership-modal.component';
import { MessageModalComponent } from './components/modals/message-modal/message-modal.component';
import { ModalComponent } from './components/modals/modal/modal.component';
import { NewExportDatabaseModalComponent } from './components/modals/new-export-database-modal/new-export-database-modal.component';
import { NewLoadFileModalComponent } from './components/modals/new-load-file-modal/new-load-file-modal.component';
import { NewOrganizationModalComponent } from './components/modals/new-organization-modal/new-organization-modal.component';
import { NewProjectModalComponent } from './components/modals/new-project-modal/new-project-modal.component';
import { NewRealmNodeEditComponent } from './components/modals/new-realm-node-edit/new-realm-node-edit.component';
import { OcrSettingsComponent } from './components/modals/ocr-settings/ocr-settings.component';
import { OtherLegalDiscoveryOptionsComponent } from './components/modals/other-legal-discovery-options-modal/other-legal-discovery-options-modal.component';
import { PolicyAcknowledgmentModalComponent } from './components/modals/policy-acknowledgment-modal/policy-acknowledgment-modal.component';
import { PatternModalComponent } from './components/modals/pattern-modal/pattern-modal.component';
import { ReindexDataSetModalComponent } from './components/modals/reindex-data-set-modal/reindex-data-set-modal.component';
import { RemoveRestoreDocsModalComponent } from './components/modals/remove-restore-docs-modal/remove-restore-docs-modal.component';
import { ReportsDetailsModalComponent } from './components/modals/reports-details-modal/reports-details-modal.component';
import { ReprocessModalComponent } from './components/modals/reprocess-modal/reprocess-modal-modal.component';
import { ExtractOffice365DataModalComponent } from './components/modals/extract-office-365-data-modal/extract-office-365-data-modal.component';
import { RunCombinedSearchModalComponent } from './components/modals/run-combined-search-modal/run-combined-search-modal.component';
import { SaveToOrgsModalComponent } from './components/modals/save-to-orgs-modal/save-to-orgs-modal.component';
import { SearchSettingsComponent } from './components/modals/settings-modal/search-settings/search-settings.component';
import { SelectAssociatedReviewProjectModalComponent } from './components/modals/select-associated-review-project-modal/select-associated-review-project-modal.component';
import { SelectReviewProjectsModalComponent } from './components/modals/select-review-projects-modal/select-review-projects-modal.component';
import { SelectSystemStorageDepotModalComponent } from './components/modals/select-system-storage-depot-modal/select-system-storage-depot-modal.component';
import { SystemStatusModalComponent } from './components/modals/system-status-modal/system-status-modal.component';
import { TagsModalComponent } from './components/modals/tags-modal/tags-modal.component';
import { TargetPickerModalComponent } from './components/modals/target-picker-modal/target-picker-modal.component';
import { TimezoneModalComponent } from './components/modals/timezone-preferences/timezone-modal.component';
import { TransformersModalComponent } from './components/modals/transformers-modal/transformers-modal.component';
import { UpdateExportVolumeModalComponent } from './components/modals/update-export-volume-modal/update-export-volume-modal.component';
import { GenerateLoadFileModalComponent } from './components/modals/generate-load-file-modal/generate-load-file-modal.component';
import { UpdateMetadataModalComponent } from './components/modals/update-metadata-modal/update-metadata-modal.component';
import { WordListModalComponent } from './components/modals/word-list-modal/word-list-modal.component';
import { WorkbasketTaskDetailsModalComponent } from './components/modals/workbasket-task-details-modal/workbasket-task-details-modal.component';
import { WorkflowQueryModalComponent } from './components/modals/workflow-query-modal/workflow-query-modal.component';
import { WorkflowReportingOptionsModalComponent } from './components/modals/workflow-reporting-options-modal/workflow-reporting-options-modal.component';

// Settings
import { AliasListsComponent } from './components/modals/settings-modal/alias-lists/alias-lists.component';
import { AnalyticSettingsComponent } from './components/modals/settings-modal/analytic-settings/analytic-settings.component';
import { BillingReportComponent } from './components/modals/settings-modal/billing-report/billing-report.component';
import { ContainerKeyFileComponent } from './components/modals/settings-modal/container-key-files/container-key-files.component';
import { CustomFieldsComponent } from './components/modals/settings-modal/custom-fields/custom-fields.component';
import { DataAreasSettingsComponent } from './components/modals/settings-modal/data-areas/data-areas.component';
import { DeletePendingProjectsComponent } from './components/modals/settings-modal/delete-pending-projects/delete-pending-projects.component';
import { DeletedProjectsSettingsComponent } from './components/modals/settings-modal/deleted-projects-settings/deleted-projects-settings.component';
import { DomainListsComponent } from './components/modals/settings-modal/domain-lists/domain-lists.component';
import { EmailDeliveryComponent } from './components/modals/settings-modal/email-delivery/email-delivery.component';
import { EmailServerComponent } from './components/modals/settings-modal/email-server/email-server.component';
import { ExcludedContentComponent } from './components/modals/settings-modal/excluded-content/excluded-content.component';
import { ExportDatabasesComponent } from './components/modals/settings-modal/export-databases/export-databases.component';
import { ExportFieldsComponent } from './components/modals/settings-modal/export-fields/export-fields.component';
import { ExportSettingsComponent } from './components/modals/settings-modal/export-settings/export-settings.component';
import { GroupsComponent } from './components/modals/settings-modal/groups/groups.component';
import { RealmNodesComponent } from './components/modals/settings-modal/realm-nodes/realm-nodes.component';
import { IndexSettingsComponent } from './components/modals/settings-modal/index-settings/index-settings.component';
import { LicenseComponent } from './components/modals/settings-modal/license/license.component';
import { LoadFileImportComponent } from './components/modals/settings-modal/load-file-import/load-file-import.component';
import { LoadFileDiscoverFieldsModalComponent } from './components/modals/settings-modal/load-file-import-discover-fields-modal/load-file-import-discover-fields-modal.component';
import { MetadataFieldsComponent } from './components/modals/settings-modal/metadata-fields/metadata-fields.component';
import { NewUserModalComponent } from './components/modals/new-user-modal/new-user-modal.component';
import { OrganizationsComponent } from './components/modals/settings-modal/organizations/organizations.component';
import { PasswordAndUserLogoutPolicyComponent } from './components/modals/settings-modal/password-and-user-logout-policy/password-and-user-logout-policy.component';
import { PasswordCrackingSettingsComponent } from './components/modals/settings-modal/password-cracking-settings/password-cracking-settings.component';
import { PatternsComponent } from './components/modals/settings-modal/patterns/patterns.component';
import { PreferencesComponent } from './components/modals/settings-modal/preferences-settings/preferences-settings.component';
import { ProjectConfigurationComponent } from './components/modals/settings-modal/project-configuration/project-configuration.component';
import { ProjectsComponent } from './components/modals/settings-modal/projects/projects.component';
import { ProjectMembersComponent } from './components/modals/settings-modal/project-members/project-members.component';
import { ReportPanelComponent } from './components/report-panel/report-panel.component';
import { ReportSettingsComponent } from './components/modals/settings-modal/report-settings/report-settings.component';
import { ReviewPromotionTrackingComponent } from './components/status-management/reef-review-import-tracking/reef-review-promotion-tracking.component';
import { RolePermissionsComponent } from './components/modals/settings-modal/role-permissions/role-permissions.component';
import { SaveAsLoadFromTemplateComponent } from './components/modals/settings-modal/save-as-load-from-template/save-as-load-from-template.component';
import { ServicesComponent } from './components/modals/settings-modal/services/services.component';
import { SettingsModalComponent } from './components/modals/settings-modal/settings-modal.component';
import { SharedDataSetsComponent } from './components/modals/settings-modal/shared-data-sets/shared-data-sets.component';
import { StorageComponent } from './components/modals/settings-modal/storage/storage.component';
import { StorageModalComponent } from './components/modals/storage-modal/storage-modal.component';
import { SurvivorshipComponent } from './components/modals/settings-modal/survivorship/survivorship.component';
import { SystemMessageComponent } from './components/modals/settings-modal/system-message/system-message.component';
import { SystemReviewComponent } from './components/modals/settings-modal/review-system/review-system.component';
import { SystemRolePermissionsComponent } from './components/modals/settings-modal/system-role-permissions/system-role-permissions.component';
import { SystemsStorageComponent } from './components/modals/settings-modal/system-storage/system-storage.component';
import { SystemStorageDepotComponent } from './components/modals/settings-modal/system-storage-depot/system-storage-depot.component';
import { TagsSettingsComponent } from './components/modals/settings-modal/tags-settings/tags-settings.component';
import { TemplateProfilesComponent } from './components/modals/settings-modal/template-profiles/template-profiles.component';
import { TlsExpressEmailListsComponent } from './components/modals/settings-modal/tls-express-email-lists/tls-express-email-lists.component';
import { UsersPermissionComponent } from './components/modals/settings-modal/users-permissions/users-permissions.component';
import { UsersAndGroupsMembershipModalComponent } from './components/modals/users-and-groups-membership-modal/users-and-groups-membership-modal.component';
import { VirusDetectionComponent } from './components/modals/settings-modal/virus-detection/virus-detection.component';
import { WorkflowsTemplateComponent } from './components/modals/settings-modal/workflows-template/workflows-template.component';
import { SelectDocumentFolderModalComponent } from './components/modals/select-document-folder-modal/select-document-folder-modal/select-document-folder-modal.component';
import { EditSharedCorpusModalComponent } from './components/modals/edit-shared-corpus-modal/edit-shared-corpus-modal.component';

// Directives
import { BusySpinnerDirective } from './directives/busySpinner/busy-spinner.directive';
import { ClickHighlightDirective } from './directives/clickHighlight/clickHighlight.directive';
import { FormErrorInputDirective } from './directives/formInput/form-input.directive';
import { IconSwitchDirective } from './directives/iconSwitch/icon-switch.directive';
import { InputFocusDirective } from './directives/inputFocus/input-focus.directive';
import { KeyValuesPipe } from './pipes/key-values.pipe';
import { ModalToolsDirective } from './directives/modal-tools/modal-tools.directive';
import { RequiredAsteriskDirective } from './directives/requiredAsterisk/required-asterisk.directive';
import { ScrollablePopoverDirective } from './directives/scrollable-popover/scrollable-popover.directive';

// Pipes
import { KeysPipe } from './pipes/keys.pipe';
import { ValuesPipe } from './pipes/values.pipe';

// Services
import { CommonService } from './services/common/common.service';
import { CommonWithRestService } from './services/common/common-with-rest.service';
import { ContextMenuComponent } from './components/context-menu/context-menu.component';
import { CookieService } from 'ngx-cookie-service';
import { DrInterceptor } from './services/http/drInterceptor';
import { HttpService } from './services/http/http.service';
import { LogoutService } from './services/logout/logout.service';
import { MetadataService } from './services/metadata/metadata.service';
import { RestService } from './services/rest/rest.service';
import { XMLService } from './services/xml/xml.service';
import { TreeDataLoaderService } from './services/tree/treeDataLoader.service';
import { ToastNotificationService } from './services/toast-notification/toast-notification.service';
import { CustomHeaderCheckboxComponent } from './components/grid-templates/custom-header-checkbox/custom-header-checkbox.component';
import { CustomSliderToggleCellRendererComponent } from './components/grid-templates/custom-slider-toggle-cell-renderer/custom-slider-toggle-cell-renderer.component';
import { DownloadService } from './services/download/download.service';
import { SelectReviewTemplateModalComponent } from './components/modals/select-review-template-modal/select-review-template-modal.component';

@NgModule({
    declarations: [
        // pipes
        KeysPipe,
        KeyValuesPipe,
        ValuesPipe,

        // grid templates
        GridHeaderComponent,
        ComboBoxCellComponent,
        DragCellComponent,
        DragDropCellComponent,

        // directives
        BusySpinnerDirective,
        ClickHighlightDirective,
        FormErrorInputDirective,
        IconSwitchDirective,
        InputFocusDirective,
        ModalToolsDirective,
        RequiredAsteriskDirective,
        ScrollablePopoverDirective,

        // components
        ActiveUserSessionsComponent,
        AnalyticSettingsComponent,
        AppComponent,
        BreadcrumbComponent,
        BulkSearchComponent,
        ClusterListComponent,
        CollapsibleToggleBoxGroupComponent,
        ConnectorsAndTreeComponent,
        ConnectorsListComponent,
        CustomCheckboxComponent,
        CustomRadioButtonComponent,
        CustomDateTimePickerComponent,
        CustomDateTimeRangePickerComponent,
        CustomComboBoxComponent,
        CustomInputComponent,
        CustomInputFileComponent,
        CustomSelectComponent,
        CustomSliderComponent,
        DataAreaAndTreeComponent,
        DocumentListComponent,
        DocumentViewerComponent,
        EmptyRouteComponent,
        EmailThreadsComponent,
        ExceptionListComponent,
        ExportComponent,
        ExportOutputFormattingOptionsComponent,
        ExportProductionImagingOptionsComponent,
        ExportStreamVolumeSettingsComponent,
        FilterComponent,
        GenericGridComponent,
        GenericInputComponent,
        GenericTreeComponent,
        JobHistoryComponent,
        JobManagementComponent,
        KeyValueListComponent,
        LoginComponent,
        MainContentComponent,
        MatrixComponent,
        MonitoringComponent,
        MetadataFiltersComponent,
        NameDescriptionComponent,
        NewDataSetLoadFileMenuComponent,
        PagerComponent,
        PatternsComponent,
        ProfileDetailComponent,
        ProjectListComponent,
        ReportPanelComponent,
        ReportsComponent,
        ResizerComponent,
        SearchComponent,
        ShortMessageThreadsComponent,
        SummaryComponent,
        StatusManagementComponent,
        SystemAuditLogComponent,
        TargetPickerTreeComponent,
        TaskFilterComponent,
        TopNavBarComponent,
        WorkbasketComponent,
        WorkflowComponent,
        WorkflowStepComponent,

        // settings
        AliasListsComponent,
        AnalyticSettingsComponent,
        BillingReportComponent,
        ConnectorsListComponent,
        CustomFieldsComponent,
        DataAreasSettingsComponent,
        DeletePendingProjectsComponent,
        DeletedProjectsSettingsComponent,
        DomainListsComponent,
        EmailDeliveryComponent,
        EmailServerComponent,
        ExcludedContentComponent,
        ExportDatabasesComponent,
        ExportSettingsComponent,
        IndexSettingsComponent,
        LicenseComponent,
        LoadFileImportComponent,
        LoadFileDiscoverFieldsModalComponent,
        MetadataFieldsComponent,
        OrganizationsComponent,
        PasswordAndUserLogoutPolicyComponent,
        PasswordCrackingSettingsComponent,
        PatternsComponent,
        PreferencesComponent,
        ProjectConfigurationComponent,
        ProjectsComponent,
        ProjectMembersComponent,
        ReportSettingsComponent,
        RolePermissionsComponent,
        SharedDataSetsComponent,
        SharedDataSetsComponent,
        SearchSettingsComponent,
        ServicesComponent,
        StorageComponent,
        SurvivorshipComponent,
        SystemMessageComponent,
        SystemRolePermissionsComponent,
        SystemsStorageComponent,
        SystemStorageDepotComponent,
        SystemReviewComponent,
        TagsSettingsComponent,
        TemplateProfilesComponent,
        TlsExpressEmailListsComponent,
        UsersPermissionComponent,
        VirusDetectionComponent,

        // modals
        AddAliasesDomainEntriesModalComponent,
        AddPasswordsModalComponent,
        AddSelectedTextToModalComponent,
        AddSharedDataSetModalComponent,
        AddToRemoveFromModalComponent,
        AdvancedSearchModalComponent,
        AuditLogModalComponent,
        BuildClustersModalComponent,
        ChangePasswordModalComponent,
        ChangeServiceModalComponent,
        ConnectorsModalComponent,
        EditSharedCorpusModalComponent,
        ConnectorPickerComponent,
        ConnectorPickerModalComponent,
        ContainerKeyFileComponent,
        CopyToDocStorageModalComponent,
        CopyToLoadFromExternalAreaModalComponent,
        CreateDatasetFromSearchResultModalComponent,
        CreateEditComparisonModalComponent,
        CreateEditEntityModalComponent,
        CreateEditEntityGroupModalComponent,
        NewRealmNodeEditComponent,
        CreateEditGroupFieldModalComponent,
        CreateSampleModalComponent,
        CreateManifestModalComponent,
        CreateNearDuplicateMetadataModalComponent,
        DataAreaModalComponent,
        DataSetConfigurationModalComponent,
        DataSetModalComponent,
        DeleteEntityModalComponent,
        DiscardPileLogComponent,
        DownloadAsPDFStampModalComponent,
        DownloadOCRUsageReportModalComponent,
        DownloadWordListModalComponent,
        EditBucketModalComponent,
        EnterSecurityCodeModalComponent,
        EndUserLicenseAgreementModalComponent,
        ExcludedContentModalComponent,
        ExportFieldsComponent,
        ExportDuplicatesVolumeModalComponent,
        ExportModalComponent,
        ExportProjectModalComponent,
        FindInTreeModalComponent,
        FoundPasswordsModalComponent,
        GenerateLoadFileModalComponent,
        GenerateMoreReportsModalComponent,
        GenerateDataSetReportModalComponent,
        GenericPickerModalComponent,
        GroupsComponent,
        RealmNodesComponent,
        ImportProjectModalComponent,
        NotificationModalComponent,
        KnowFrequentlyUsedPasswordsModalComponent,
        LiveLogModalComponent,
        KeyFileModalComponent,
        LeftRightSelectionBoxComponent,
        MembershipModalComponent,
        MessageModalComponent,
        ModalComponent,
        NewExportDatabaseModalComponent,
        NewLoadFileModalComponent,
        NewOrganizationModalComponent,
        NewProjectModalComponent,
        OcrSettingsComponent,
        NewUserModalComponent,
        OtherLegalDiscoveryOptionsComponent,
        PolicyAcknowledgmentModalComponent,
        PatternModalComponent,
        ProjectAccessHistoryComponent,
        ReindexDataSetModalComponent,
        RemoveRestoreDocsModalComponent,
        ReportsDetailsModalComponent,
        ReprocessModalComponent,
        ExtractOffice365DataModalComponent,
        RunCombinedSearchModalComponent,
        SaveAsLoadFromTemplateComponent,
        SaveToOrgsModalComponent,
        SettingsModalComponent,
        SelectAssociatedReviewProjectModalComponent,
        SelectReviewProjectsModalComponent,
        SelectReviewTemplateModalComponent,
        SelectSystemStorageDepotModalComponent,
        StorageModalComponent,
        SystemStatusModalComponent,
        TagsModalComponent,
        TargetPickerModalComponent,
        TimezoneModalComponent,
        TransformersModalComponent,
        ToastNotificationComponent,
        UpdateExportVolumeModalComponent,
        UpdateMetadataModalComponent,
        UsersAndGroupsMembershipModalComponent,
        WordListModalComponent,
        WorkbasketTaskDetailsModalComponent,
        WorkflowQueryModalComponent,
        WorkflowReportingOptionsModalComponent,
        ContextMenuComponent,
        CustomHeaderCheckboxComponent,
        CustomSliderToggleCellRendererComponent,
        SelectDocumentFolderModalComponent,
        ReviewPromotionTrackingComponent,
        WorkflowsTemplateComponent,
        GridCellToggleSwitchComponent,
        ExportToReviewModalComponent
    ],
    imports: [
        // modules
        AgGridModule,
        // AgGridModule.withComponents([
        //     GridHeaderComponent,
        //     ComboBoxCellComponent,
        //     DragCellComponent,
        //     DragDropCellComponent
        // ]),
        CdkTreeModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        BrowserModule,
        BsDropdownModule.forRoot(),
        BsDatepickerModule.forRoot(),
        ButtonsModule.forRoot(),
        CommonModule,
        DragDropModule,
        FormsModule.withConfig({ callSetDisabledState: 'whenDisabledForLegacyCode' }),
        HighlightModule,
        HttpClientModule,
        ModalModule.forRoot(),
        DndModule,
        PopoverModule.forRoot(),
        ReactiveFormsModule.withConfig({ callSetDisabledState: 'whenDisabledForLegacyCode' }),
        ScrollingModule,
        OverlayModule
    ],
    providers: [
        BsModalRef,
        TitleCasePipe,

        // services
        CommonService,
        CommonWithRestService,
        CookieService,
        HttpService,
        LogoutService,
        MetadataService,
        RestService,
        DownloadService,
        TreeDataLoaderService,
        ToastNotificationService,
        ContextMenuComponent,
        XMLService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DrInterceptor,
            multi: true
        },
        {
            provide: HIGHLIGHT_OPTIONS,
            useValue: {
                coreLibraryLoader: () => import('highlight.js/lib/core'),
            }
        },
        {
            provide: APP_BASE_HREF,
            useValue: window.location.pathname,
        }
    ],
    bootstrap: [AppComponent],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA
    ]
})
export class AppModule { }
